import { useEffect, useState } from 'react';
import LockedPairsModal from './LockedPairsModal';
import AdminPageModal from './AdminPageModal';
import '../css/AdminSidebar.css';

function AdminSidebar({ active = '', groupId, groupName = '', onRefresh }) {
  const [lockedPairsOpen, setLockedPairsOpen] = useState(false);
  const [pageModal, setPageModal] = useState(null);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [openSections, setOpenSections] = useState(() => ({ competition: true }));
  const selectedGroupId = Number(groupId || localStorage.getItem('last_group_id') || 0);
  const embedMode = new URLSearchParams(window.location.search).get('embed') === '1';

  useEffect(() => {
    if (!embedMode) return undefined;
    document.body.classList.add('admin-embed-mode');
    return () => document.body.classList.remove('admin-embed-mode');
  }, [embedMode]);

  const openPage = path => window.open(path, '_blank', 'noopener,noreferrer');
  const openHere = path => window.location.assign(path);
  const sections = [
    { key: 'competition', label: 'การแข่งขัน', icon: '🏸', items: [
      { key: 'game-control', icon: '🎲', label: 'Game Match Control', disabled: !selectedGroupId, action: () => openHere(`/game-match-control/${selectedGroupId}`) },
      { key: 'planner', icon: '✨', label: 'Match Planner', disabled: !selectedGroupId, action: () => openPage(`/match-planner/${selectedGroupId}`) },
      { key: 'create-match', icon: '➕', label: 'จัดแมท', disabled: !selectedGroupId, action: () => setPageModal({ title: 'จัดแมท', url: `/match-planner/${selectedGroupId}?manual=1&embed=1` }) },
      { key: 'lock-pair', icon: '🔒', label: 'Lock Pair', disabled: !selectedGroupId, action: () => setLockedPairsOpen(true) },
      { key: 'matches', icon: '🛡️', label: 'Group Matches', disabled: !selectedGroupId, action: () => setPageModal({ title: 'Group Matches', url: `/admin/group/${selectedGroupId}/matches?embed=1` }) }
    ]},
    { key: 'members', label: 'สมาชิก', icon: '👥', items: [
      { key: 'checkin', icon: '▦', label: 'Check-in', disabled: !selectedGroupId, action: () => openPage(`/admin/checkin/${selectedGroupId}`) },
      { key: 'users', icon: '🛡️', label: 'ผู้เล่นทั้งหมด', action: () => setPageModal({ title: 'ผู้เล่นทั้งหมด', url: '/admin/users?embed=1' }) },
      { key: 'line-links', icon: 'LINE', label: 'ผูก LINE กับ Player ID', action: () => setPageModal({ title: 'ผูก LINE กับ Player ID', url: '/admin/line-links?embed=1' }) },
      { key: 'user-types', icon: '🏷️', label: 'กำหนด Type User', disabled: !selectedGroupId, action: () => setPageModal({ title: 'กำหนด Type User', url: `/admin/users?mode=type&groupId=${selectedGroupId}&embed=1` }) }
    ]},
    { key: 'monitoring', label: 'Monitor & QR', icon: '🖥️', items: [
      { key: 'monitor', icon: '🖥️', label: 'Admin Monitor', action: () => openPage('/admin_monitor') },
      { key: 'checkin-qr', icon: '▦', label: 'สร้าง Check-in QR', action: () => setPageModal({ title: 'สร้าง Check-in QR', url: '/admin/checkin-qr?embed=1' }) },
      { key: 'monitor-qr', icon: '📺', label: 'สร้าง QR Monitor', action: () => setPageModal({ title: 'สร้าง QR Monitor', url: '/admin/monitor-qr?embed=1' }) }
    ]},
    { key: 'reports', label: 'รายงาน & Reward', icon: '📊', items: [
      { key: 'result-logs', icon: '📝', label: 'Result Log', disabled: !selectedGroupId, action: () => setPageModal({ title: 'Result Log', url: `/admin/result-logs?groupId=${selectedGroupId}&embed=1` }) },
      { key: 'rewards', icon: '🎁', label: 'จัดการรางวัล', action: () => setPageModal({ title: 'จัดการรางวัล', url: '/admin/rewards?embed=1' }) },
      { key: 'reward-confirm', icon: '📷', label: 'ยืนยันรับของรางวัล', action: () => openHere('/rewards?adminScan=1') }
    ]}
  ];

  const runMobileAction = action => {
    setMobileMoreOpen(false);
    action?.();
  };

  if (embedMode) return null;

  return <>
    <aside className="admin-shared-sidebar" aria-label="เมนูผู้ดูแลระบบ">
      <strong>🏸 Admin<br />Dashboard</strong>
      <div className="admin-current-group">
        <small>ก๊วนที่กำลังจัดการ</small>
        <b>{selectedGroupId ? (groupName || `Group #${selectedGroupId}`) : 'ยังไม่ได้เลือกก๊วน'}</b>
        <button type="button" onClick={() => openHere('/dashboard')}>⇄ เลือกก๊วน</button>
      </div>
      <nav>
        <button type="button" className={`admin-home-link ${active === 'dashboard' ? 'is-active' : ''}`} onClick={() => openHere('/dashboard')}><span>🏠</span>ภาพรวม</button>
        {sections.map(section => {
          const expanded = Boolean(openSections[section.key]) || section.items.some(item => item.key === active);
          return <section className="admin-nav-section" key={section.key}>
            <button type="button" className="admin-nav-section-toggle" onClick={() => setOpenSections(current => ({ ...current, [section.key]: !expanded }))} aria-expanded={expanded}>
              <span>{section.icon}</span>{section.label}<i>{expanded ? '−' : '+'}</i>
            </button>
            {expanded && <div className="admin-nav-section-items">{section.items.map(item => <button type="button" key={item.key} className={active === item.key ? 'is-active' : ''} disabled={item.disabled} onClick={item.action}><span>{item.icon}</span>{item.label}</button>)}</div>}
          </section>;
        })}
      </nav>
      {onRefresh && <button type="button" className="admin-sidebar-refresh" onClick={onRefresh}>🔄 Refresh</button>}
    </aside>
    <nav className="admin-mobile-bottom-nav" aria-label="เมนูหลักสำหรับมือถือและแท็บเล็ต">
      <button type="button" className={active === 'dashboard' ? 'is-active' : ''} onClick={() => openHere('/dashboard')}><span>⌂</span><b>ภาพรวม</b></button>
      <button type="button" className={active === 'game-control' ? 'is-active' : ''} disabled={!selectedGroupId} onClick={() => openHere(`/game-match-control/${selectedGroupId}`)}><span>🎲</span><b>จัดแมตช์</b></button>
      <button type="button" className={active === 'lock-pair' || lockedPairsOpen ? 'is-active' : ''} disabled={!selectedGroupId} onClick={() => setLockedPairsOpen(true)}><span>🔒</span><b>Lock Pair</b></button>
      <button type="button" className={mobileMoreOpen ? 'is-active' : ''} onClick={() => setMobileMoreOpen(open => !open)} aria-expanded={mobileMoreOpen}><span>•••</span><b>เพิ่มเติม</b></button>
    </nav>
    {mobileMoreOpen && <div className="admin-mobile-more-backdrop" onClick={() => setMobileMoreOpen(false)}>
      <section className="admin-mobile-more" role="dialog" aria-modal="true" aria-label="เครื่องมือเพิ่มเติม" onClick={event => event.stopPropagation()}>
        <header><div><small>เครื่องมือผู้จัด</small><h2>เมนูเพิ่มเติม</h2></div><button type="button" onClick={() => setMobileMoreOpen(false)} aria-label="ปิดเมนู">×</button></header>
        {selectedGroupId && <div className="admin-mobile-group"><span>ก๊วนที่กำลังจัดการ</span><b>{groupName || `Group #${selectedGroupId}`}</b></div>}
        <div className="admin-mobile-more-scroll">
          {sections.map(section => <section className="admin-mobile-tool-section" key={`mobile-${section.key}`}>
            <h3><span>{section.icon}</span>{section.label}</h3>
            <div>{section.items.filter(item => !['game-control', 'planner'].includes(item.key)).map(item => <button type="button" key={item.key} disabled={item.disabled} onClick={() => runMobileAction(item.action)}><span>{item.icon}</span><b>{item.label}</b><i>›</i></button>)}</div>
          </section>)}
          {onRefresh && <button type="button" className="admin-mobile-refresh" onClick={() => runMobileAction(onRefresh)}>🔄 รีเฟรชข้อมูล</button>}
        </div>
      </section>
    </div>}
    {lockedPairsOpen && <LockedPairsModal groupId={selectedGroupId} onClose={() => setLockedPairsOpen(false)} />}
    {pageModal && <AdminPageModal title={pageModal.title} url={pageModal.url} onClose={() => setPageModal(null)} />}
  </>;
}

export default AdminSidebar;
